/**
 * Third-person camera. Internal – use CameraManager.createThirdPersonCamera().
 * Orbits behind target; zoom; optional follow-target-rotation; touch/joystick.
 */

import * as THREE from "three";
import { CameraController } from "../core/CameraController";
import { CameraMode, type ThirdPersonCameraConfig } from "../types/CameraTypes";

export class ThirdPersonCamera extends CameraController {
  private distance: number;
  private minDistance: number;
  private maxDistance: number;
  private heightOffset: number;
  private yaw: number;
  private pitch: number;
  private mouseSensitivity: number;
  private followTargetRotation: boolean;
  private enableZoom: boolean;
  private zoomSpeed: number;
  private lookAtOffset: THREE.Vector3 = new THREE.Vector3(0, 1, 0);

  constructor(
    camera: THREE.PerspectiveCamera | THREE.OrthographicCamera,
    config: ThirdPersonCameraConfig = {}
  ) {
    super(camera, config);

    const thirdPersonConfig: Required<
      Omit<ThirdPersonCameraConfig, "inputProvider">
    > = {
      smoothing: config.smoothing ?? true,
      smoothFactor: config.smoothFactor ?? 0.1,
      minPitch: config.minPitch ?? 0.1,
      maxPitch: config.maxPitch ?? Math.PI / 2 - 0.1,
      enableCollision: config.enableCollision ?? false,
      distance: config.distance ?? 8,
      minDistance: config.minDistance ?? 2,
      maxDistance: config.maxDistance ?? 20,
      heightOffset: config.heightOffset ?? 3,
      mouseSensitivity: config.mouseSensitivity ?? 0.003,
      followTargetRotation: config.followTargetRotation ?? false,
      enableZoom: config.enableZoom ?? true,
      zoomSpeed: config.zoomSpeed ?? 1.0,
      initialYaw: config.initialYaw ?? 0,
      initialPitch: config.initialPitch ?? 0.3,
    };

    this.distance = thirdPersonConfig.distance;
    this.minDistance = thirdPersonConfig.minDistance;
    this.maxDistance = thirdPersonConfig.maxDistance;
    this.heightOffset = thirdPersonConfig.heightOffset;
    this.mouseSensitivity = thirdPersonConfig.mouseSensitivity;
    this.followTargetRotation = thirdPersonConfig.followTargetRotation;
    this.enableZoom = thirdPersonConfig.enableZoom;
    this.zoomSpeed = thirdPersonConfig.zoomSpeed;
    this.yaw = thirdPersonConfig.initialYaw;
    this.pitch = thirdPersonConfig.initialPitch;
  }

  get mode(): CameraMode {
    return CameraMode.THIRD_PERSON as CameraMode;
  }

  update(deltaTime: number): void {
    if (!this.target) return;

    // Get input from provider
    if (this.inputProvider) {
      const mouseDelta = this.inputProvider.getMouseDelta();
      const wheelDelta = this.inputProvider.getMouseWheelDelta();

      // Handle mouse input for camera rotation (always follows mouse movement)
      if (mouseDelta.x !== 0 || mouseDelta.y !== 0) {
        this.yaw -= mouseDelta.x * this.mouseSensitivity;
        this.pitch += mouseDelta.y * this.mouseSensitivity;
        this.pitch = this.clampPitch(this.pitch);
      }

      // Handle zoom with mouse wheel
      if (this.enableZoom) {
        this.distance -= wheelDelta.y * this.zoomSpeed * 0.01;
        this.distance = Math.max(
          this.minDistance,
          Math.min(this.maxDistance, this.distance)
        );
      }

      // ===== MOBILE TOUCH CONTROLS =====
      // Handle touch input for mobile devices
      if (
        "isTouchActive" in this.inputProvider &&
        typeof this.inputProvider.isTouchActive === "function"
      ) {
        const isTouching = this.inputProvider.isTouchActive();
        const touchCount = this.inputProvider.getTouchCount
          ? this.inputProvider.getTouchCount()
          : 0;

        if (isTouching && touchCount === 1) {
          // Single touch: Rotate camera (like mouse movement)
          const touchDelta = this.inputProvider.getTouchDelta
            ? this.inputProvider.getTouchDelta()
            : { x: 0, y: 0 };

          if (touchDelta.x !== 0 || touchDelta.y !== 0) {
            this.yaw -= touchDelta.x * this.mouseSensitivity;
            this.pitch += touchDelta.y * this.mouseSensitivity;
            this.pitch = this.clampPitch(this.pitch);
          }
        } else if (isTouching && touchCount === 2) {
          // Two-finger pinch: Zoom
          if (this.enableZoom && this.inputProvider.getPinchDelta) {
            const pinchDelta = this.inputProvider.getPinchDelta();
            if (pinchDelta !== 0) {
              // Pinch delta is in pixels, convert to zoom amount
              const zoomAmount = -pinchDelta * 0.01 * this.zoomSpeed;
              this.distance += zoomAmount;
              this.distance = Math.max(
                this.minDistance,
                Math.min(this.maxDistance, this.distance)
              );
            }
          }
        }
      }

      // ===== VIRTUAL JOYSTICK CONTROLS =====
      // Handle joystick input for camera rotation (generic - works with any joystick ID)
      if (
        "getJoystickVector" in this.inputProvider &&
        typeof this.inputProvider.getJoystickVector === "function"
      ) {
        // Check for common joystick IDs (right joystick for camera rotation)
        const joystickIds = ["camera", "right", "look"];

        for (const joyId of joystickIds) {
          const joystickVector = this.inputProvider.getJoystickVector(joyId);
          const isActive = this.inputProvider.isJoystickActive
            ? this.inputProvider.isJoystickActive(joyId)
            : false;

          if (isActive && (joystickVector.x !== 0 || joystickVector.y !== 0)) {
            // Use joystick for camera rotation
            // X-axis controls yaw, Y-axis controls pitch
            const joystickSensitivity = 7.0; // Increased for faster rotation

            this.yaw -=
              joystickVector.x *
              this.mouseSensitivity *
              joystickSensitivity *
              deltaTime *
              60;
            this.pitch +=
              joystickVector.y *
              this.mouseSensitivity *
              joystickSensitivity *
              deltaTime *
              60;
            this.pitch = this.clampPitch(this.pitch);

            break; // Only use first active joystick
          }
        }
      }
    }

    // Follow target rotation if enabled
    if (this.followTargetRotation && this.target.rotation) {
      // Smoothly rotate camera to match target's forward direction
      const targetYaw = this.target.rotation.y;
      const yawDiff = this.normalizeAngle(targetYaw - this.yaw);
      this.yaw += yawDiff * 0.1; // Smooth follow
    }

    // Calculate camera position using spherical coordinates
    const horizontalDistance = this.distance * Math.cos(this.pitch);
    const verticalDistance = this.distance * Math.sin(this.pitch);

    const offsetX = horizontalDistance * Math.sin(this.yaw);
    const offsetZ = horizontalDistance * Math.cos(this.yaw);
    const offsetY = verticalDistance + this.heightOffset;

    // Set target position for camera
    this.targetPosition.set(
      this.target.position.x - offsetX,
      this.target.position.y + offsetY,
      this.target.position.z - offsetZ
    );

    // Apply smoothing
    this.applySmoothPosition(deltaTime);

    // Look at target (with slight offset for better view)
    const lookAtPoint = new THREE.Vector3(
      this.target.position.x + this.lookAtOffset.x,
      this.target.position.y + this.lookAtOffset.y,
      this.target.position.z + this.lookAtOffset.z
    );
    this.camera.lookAt(lookAtPoint);
  }

  reset(): void {
    if (this.target) {
      // Reset to default position behind target
      const horizontalDistance = this.distance * Math.cos(this.pitch);
      const verticalDistance = this.distance * Math.sin(this.pitch);

      const offsetX = horizontalDistance * Math.sin(this.yaw);
      const offsetZ = horizontalDistance * Math.cos(this.yaw);
      const offsetY = verticalDistance + this.heightOffset;

      this.camera.position.set(
        this.target.position.x - offsetX,
        this.target.position.y + offsetY,
        this.target.position.z - offsetZ
      );

      this.currentPosition.copy(this.camera.position);
      this.targetPosition.copy(this.camera.position);

      const lookAtPoint = this.target.position.clone().add(this.lookAtOffset);
      this.camera.lookAt(lookAtPoint);
    }
  }

  /** Set yaw/pitch (e.g. on init). */
  setRotation(yaw: number, pitch: number): void {
    this.yaw = yaw;
    this.pitch = this.clampPitch(pitch);
  }

  getRotation(): { yaw: number; pitch: number } {
    return { yaw: this.yaw, pitch: this.pitch };
  }

  setDistance(distance: number): void {
    this.distance = Math.max(
      this.minDistance,
      Math.min(this.maxDistance, distance)
    );
  }

  getDistance(): number {
    return this.distance;
  }

  setLookAtOffset(offset: THREE.Vector3): void {
    this.lookAtOffset.copy(offset);
  }

  /** Forward in XZ (for movement). */
  getForwardDirection(): THREE.Vector3 {
    if (!this.target) return new THREE.Vector3(0, 0, 1);

    const direction = new THREE.Vector3();
    direction.subVectors(this.target.position, this.camera.position);
    direction.y = 0; // Project to horizontal plane
    direction.normalize();
    return direction;
  }

  /** Yaw in radians (for movement). */
  getYaw(): number {
    return this.yaw;
  }
}
